import React, { useCallback, useState } from 'react';
import { FiUpload, FiX, FiImage } from 'react-icons/fi';

interface AttachmentDropzoneProps {
  attachments: File[];
  onAttachmentsChange: (files: File[]) => void;
  maxFiles?: number;
}

export const AttachmentDropzone: React.FC<AttachmentDropzoneProps> = ({ 
  attachments, 
  onAttachmentsChange, 
  maxFiles = 5 
}) => {
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleFiles = useCallback((newFiles: File[]) => {
    // Filter for image files only
    const imageFiles = newFiles.filter(file => 
      file.type === 'image/png' || file.type === 'image/jpeg' || file.type === 'image/jpg'
    );

    // Check file size (10MB limit)
    const validFiles = imageFiles.filter(file => file.size <= 10 * 1024 * 1024);

    // Combine with existing attachments, respecting max limit
    const combined = [...attachments, ...validFiles];
    const limited = combined.slice(0, maxFiles);

    onAttachmentsChange(limited);

    // Show warnings for invalid files
    if (imageFiles.length !== newFiles.length) {
      alert('Only PNG and JPEG images are allowed.');
    }
    if (validFiles.length !== imageFiles.length) {
      alert('Some files were too large. Maximum file size is 10MB.');
    }
    if (combined.length > maxFiles) {
      alert(`Maximum ${maxFiles} files allowed. Some files were not added.`);
    }
  }, [attachments, onAttachmentsChange, maxFiles]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  }, [handleFiles]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      handleFiles(files);
    }
  };

  const removeFile = (index: number) => {
    const newAttachments = attachments.filter((_, i) => i !== index);
    onAttachmentsChange(newAttachments);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-4">
      {/* Dropzone */}
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragActive
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <FiUpload className="w-8 h-8 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
        <p className="text-gray-600 dark:text-gray-400 mb-2">
          Drop screenshots here or{' '}
          <label className="text-blue-600 dark:text-blue-400 cursor-pointer hover:underline">
            browse files
            <input
              type="file"
              multiple
              accept="image/png,image/jpeg,image/jpg"
              onChange={handleFileInput}
              className="hidden"
            />
          </label>
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          PNG/JPEG only, max 10MB per file, up to {maxFiles} files
        </p>
      </div>

      {/* File List */}
      {attachments.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            Attachments ({attachments.length}/{maxFiles})
          </h4>
          <div className="space-y-2">
            {attachments.map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <FiImage className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => removeFile(index)}
                  className="p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                  aria-label={`Remove ${file.name}`}
                >
                  <FiX className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}; 